import { memo, type ReactElement, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  alpha,
  useTheme,
  type Palette,
  type SxProps,
  type Theme,
} from "@mui/material/styles";

export type ClinicalCrmContextInfoBannerVariant =
  | "success"
  | "warning"
  | "info"
  | "error";

export interface ClinicalCrmContextInfoBannerProps {
  title: string;
  description: string | ReactNode;
  icon: ReactElement;
  variant: ClinicalCrmContextInfoBannerVariant;
  sx?: SxProps<Theme>;
}

/** Opacidades alinhadas ao banner original (primary) por variante de feedback. */
const VARIANT_STYLE_OPACITY = {
  border: 0.22,
  bgcolor: 0.05,
  gradientStart: 0.08,
  gradientEnd: 0.02,
  iconBg: 0.12,
} as const;

const getVariantMainColor = (
  palette: Palette,
  variant: ClinicalCrmContextInfoBannerVariant,
): string => palette[variant].main;

const ClinicalCrmContextInfoBanner = memo(
  ({ title, description, icon, variant, sx }: ClinicalCrmContextInfoBannerProps) => {
    const theme = useTheme();
    const mainColor = getVariantMainColor(theme.palette, variant);
    const titleColor = `${variant}.main` as const;

    return (
      <Box
        sx={{
          mb: 1.5,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(mainColor, VARIANT_STYLE_OPACITY.border),
          bgcolor: alpha(mainColor, VARIANT_STYLE_OPACITY.bgcolor),
          backgroundImage: `linear-gradient(135deg, ${alpha(mainColor, VARIANT_STYLE_OPACITY.gradientStart)} 0%, ${alpha(mainColor, VARIANT_STYLE_OPACITY.gradientEnd)} 55%, transparent 100%)`,
          ...sx,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="center"
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(mainColor, VARIANT_STYLE_OPACITY.iconBg),
              color: titleColor,
            }}
          >
            {icon}
          </Box>

          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={700} color={titleColor}>
              {title}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              component={typeof description === "string" ? "p" : "div"}
              sx={{ lineHeight: 1.55 }}
            >
              {description}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  },
);

ClinicalCrmContextInfoBanner.displayName = "ClinicalCrmContextInfoBanner";

export default ClinicalCrmContextInfoBanner;
